import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { later, next, scheduleOnce } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { ComponentReturn, DirectInvokable } from '@glint/template/-private/integration';
import $ from 'jquery';

// This component is used on some certain event (depending on the place - currently
// transition or a modal close) to check if user has some unsaved data,
// then, if the given checkIsDirty evaluates true, will show "leave confirm"
// from there, user has options to save or discard (which will resume
// the blocked action - (transition or modal close)
import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type {
	Args as SubmitButtonArgs,
	SubmitButtonComponentInterface,
} from 'webapp/components/submit-button';
import SubmitButtonComponent from 'webapp/components/submit-button';

export interface Args {
	save: () => void;
	checkIsDirty: () => boolean;
	slideFromRight?: boolean; // if true, instead of sliding left to right, will slide from right to left (case when button is on the right)
}

type YieldedArgs = Omit<SubmitButtonArgs, 'onTrigger'>;

export interface FormLeaveConfirmBaseComponentInterface<T> {
	Args: T;
	Blocks: {
		default: [
			controls: {
				save: DirectInvokable<
					(
						args: YieldedArgs,
					) => ComponentReturn<SubmitButtonComponentInterface<YieldedArgs>>
				>;
			},
		];
	}; // this is needed for yield
}

export default abstract class FormLeaveConfirmBaseComponent<
	T extends FormLeaveConfirmBaseComponentInterface<Args>,
> extends Component<T> {
	@tracked _showLeaveConfirm = false;
	@tracked isLeaveConfirmed = false;

	node: HTMLElement | null = null;

	submitButtonComponent = SubmitButtonComponent;

	get showLeaveConfirm() {
		return this._showLeaveConfirm;
	}

	set showLeaveConfirm(value: boolean) {
		this._showLeaveConfirm = value;
		const controlsNode = this.node?.closest('.controls2');
		if (controlsNode) {
			controlsNode.classList.toggle('controls2--has-leave-confirm', value);
		}
		if (value) {
			scheduleOnce('afterRender', this, this.slideOutLeaveConfirmBar);
			scheduleOnce('afterRender', this, this.flashConfirmBar);
			scheduleOnce('afterRender', this, this.scrollToLeaveConfirmElement);
		}
	}

	scrollToLeaveConfirmElement(): void {
		next(() => {
			const leaveConfirmEl = this.node?.querySelector('.leave-confirm');
			if (leaveConfirmEl) {
				leaveConfirmEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
			}
		});
	}

	slideOutLeaveConfirmBar() {
		const confirmBarNode = this.node?.querySelector('.leave-confirm');
		if (confirmBarNode) {
			const animationProps = this.args.slideFromRight
				? { marginRight: -8 }
				: { marginLeft: -8 };
			$(confirmBarNode).animate(animationProps, 300);
		}
	}

	flashConfirmBar() {
		const controlsNode = this.node?.closest('.controls2');
		if (controlsNode) {
			controlsNode.classList.add('flash');
			later(() => {
				controlsNode.classList.remove('flash');
			}, 500);
		}
	}

	checkIsDirty(): boolean {
		return this.args.checkIsDirty();
	}

	@action
	triggerLeaveConfirm(): void {
		this.showLeaveConfirm = false;
		this.isLeaveConfirmed = true;
	}

	@action
	triggerSave(): void {
		this.showLeaveConfirm = false;
		this.isLeaveConfirmed = false;

		this.args.save();
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;
	}

	@action
	teardownView(): void {
		// maybe extended
	}
	<template>
		<div
			class="leave-confirm-wrapper"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			{{! @glint-nocheck no multi-line ignore, so we have to disable it for the whole file }}
			{{yield (hash save=(component this.submitButtonComponent onTrigger=this.triggerSave))}}

			{{#if this.showLeaveConfirm}}
				<div class="leave-confirm">
					<span class="unsaved">
						<CommonUiIconComponent @name="AlertFill" @size="xs" />You have unsaved
						changes
					</span>
					<a role="button" class="cancel" {{on "click" this.triggerLeaveConfirm}}>
						<CommonUiIconComponent @name="Trash" @size="xs" />Discard
					</a>
				</div>
			{{/if}}

		</div>
	</template>
}
